import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Bell, CalendarDays, ExternalLink, Inbox, LayoutDashboard, Layers, Moon, Plug, Sun,
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useTheme } from "../../lib/theme";
import { api } from "../../lib/api";

/**
 * The events ADMIN console shell, skinned as a Foundry module.
 *
 * Foundry (vdock-dock) is the console this app is launched from, and to its
 * users the events admin is one more module of it — so the chrome is Foundry's:
 * left sidebar with the venturedock brand block, topbar with a Foundry
 * breadcrumb, the same dark-default/light-optional theme. The look comes from
 * styles/foundry.css, which is scoped to the `.foundry` wrapper below so the
 * PUBLIC events pages keep their own light-glass identity.
 *
 * Pages pass their own header through `eyebrow` / `title` / `subtitle` /
 * `actions` and render their body as children.
 */

/** Where "Back to Foundry" points. Overridable per environment. */
const FOUNDRY_URL = process.env.REACT_APP_FOUNDRY_URL || "https://foundry.venturedock.com";

const NAV = [
  { to: "/admin/community", label: "Community", icon: LayoutDashboard },
  { to: "/admin/events", label: "Events", icon: CalendarDays },
  { to: "/admin/sessions", label: "Requests", icon: Inbox, badgeKey: "requests" },
  { to: "/admin/programs", label: "Programs", icon: Layers },
  { to: "/admin/integrations", label: "Integrations", icon: Plug },
];

/** Longest-prefix match, so /admin/events/:id/manage still reads "Events". */
function labelForPath(pathname) {
  const match = [...NAV].sort((a, b) => b.to.length - a.to.length).find((i) => pathname.startsWith(i.to));
  return match?.label ?? "Events Admin";
}

function initials(user) {
  const first = user?.first_name?.[0] ?? "";
  const last = user?.last_name?.[0] ?? "";
  const both = (first + last).toUpperCase();
  return both || (user?.email?.[0] ?? "?").toUpperCase();
}

function Sidebar({ pendingRequests }) {
  return (
    <aside className="sidebar" data-testid="admin-sidebar">
      <div className="brand">
        <Link to="/admin/community" className="brand-mark" data-testid="brand-logo">
          <span className="brand-venture">venture</span>
          <span className="brand-dock">dock</span>
          <span className="brand-sep">/</span>
        </Link>
        <div className="brand-context">Internal · Foundry</div>
      </div>

      <div className="nav-group">
        <div className="nav-label">Events admin</div>
        {NAV.map(({ to, label, icon: Icon, badgeKey }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/admin/community"}
            className={({ isActive }) => (isActive ? "nav-item active" : "nav-item")}
            data-testid={`nav-${label.toLowerCase()}`}
          >
            <Icon className="nav-icon" aria-hidden />
            {label}
            {badgeKey === "requests" && pendingRequests > 0 && (
              <span className="nav-badge alert" data-testid="requests-badge">{pendingRequests}</span>
            )}
          </NavLink>
        ))}
      </div>

      <div className="sidebar-footer">
        <a className="nav-item" href={FOUNDRY_URL} data-testid="back-to-foundry">
          <ExternalLink className="nav-icon" aria-hidden />
          Back to Foundry
        </a>
      </div>
    </aside>
  );
}

function Notifications() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  }, [loc.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const unread = items.filter((n) => !n.read).length;

  return (
    <div className="user-menu" ref={ref}>
      <button
        className="icon-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
        data-testid="notifications-btn"
      >
        <Bell size={16} />
        {unread > 0 && <span className="icon-btn-dot" />}
      </button>
      {open && (
        <div className="topbar-dropdown" role="menu" data-testid="notifications-panel">
          <div className="notif-head">
            <span className="nav-label" style={{ padding: 0 }}>Notifications</span>
            <span style={{ fontSize: 11, color: "var(--fd-text-muted)" }}>{unread} unread</span>
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto", marginTop: 6 }}>
            {items.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "var(--fd-text-muted)", fontStyle: "italic" }}>
                All caught up
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  className="notif-row"
                  data-testid={`notif-${n.id}`}
                  onClick={async () => {
                    await api.post(`/notifications/${n.id}/read`).catch(() => {});
                    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
                    if (n.link) nav(n.link);
                    setOpen(false);
                  }}
                >
                  <div className="notif-title">{n.title}</div>
                  <div className="notif-body">{n.body}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;

  return (
    <div className="user-menu" ref={ref}>
      <button
        className="avatar"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || user.email}
        data-testid="user-menu-btn"
      >
        {initials(user)}
      </button>
      {open && (
        <div className="user-dropdown" role="menu" data-testid="user-menu">
          <div className="user-dropdown-name">{user.first_name} {user.last_name}</div>
          <div className="user-dropdown-email">{user.email}</div>
          <div className="user-dropdown-role">{user.role}</div>
          <button
            className="user-dropdown-action"
            role="menuitem"
            onClick={() => { setOpen(false); logout(); nav("/"); }}
            data-testid="logout-btn"
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

function Topbar({ current }) {
  const { theme, toggle } = useTheme();
  return (
    <header className="topbar" data-testid="top-nav">
      <div className="breadcrumb">
        <a href={FOUNDRY_URL}>Foundry</a>
        <span className="crumb-sep">/</span>
        <span>Events</span>
        <span className="crumb-sep">/</span>
        <span className="crumb-current">{current}</span>
      </div>

      <Notifications />

      <button
        className="icon-btn"
        onClick={toggle}
        aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
        title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
        data-testid="theme-toggle"
      >
        {theme === "dark" ? <Moon size={17} strokeWidth={1.5} /> : <Sun size={17} strokeWidth={1.5} />}
      </button>

      <UserMenu />
    </header>
  );
}

export default function AdminShell({ eyebrow, title, subtitle, actions, children, testId }) {
  const loc = useLocation();
  const [pendingRequests, setPendingRequests] = useState(0);
  const current = useMemo(() => labelForPath(loc.pathname), [loc.pathname]);

  // Pending-approvals badge on the Requests nav item: quiet at zero, glanceable
  // when work is waiting.
  useEffect(() => {
    api.get("/admin/requests/count")
      .then((r) => setPendingRequests(r.data?.count || 0))
      .catch(() => {});
  }, [loc.pathname]);

  return (
    <div className="foundry" data-testid={testId}>
      <div className="app">
        <Sidebar pendingRequests={pendingRequests} />
        <Topbar current={current} />
        <main className="main">
          <div className="module">
            {(title || actions) && (
              <header className="page-header">
                <div className="page-title-block">
                  {eyebrow && <div className="page-eyebrow">{eyebrow}</div>}
                  {title && <h1 className="page-title">{title}</h1>}
                  {subtitle && <p className="page-subtitle">{subtitle}</p>}
                </div>
                {actions && <div className="page-actions">{actions}</div>}
              </header>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
